import React, { useState, useEffect } from 'react';
import './bulkupload.css';

const BulkUploadModal = ({ isOpen, onClose, onSubmit, type }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreviewData([]);
      setError('');
      setUploading(false);
      setProgress(0);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      setError('Please upload a valid CSV file.');
      setFile(null);
      setPreviewData([]);
      return;
    }

    setFile(selectedFile);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim()));
      const headers = rows[0]?.map(h => h.toLowerCase());
      const requiredHeaders = ['name', 'age', 'facility', 'lga', 'jobrole'];

      if (!headers || !requiredHeaders.every(h => headers.includes(h))) {
        setError('Invalid CSV format. Required headers: name, age, facility, lga, jobRole');
        setPreviewData([]);
        return;
      }

      const data = rows.slice(1).filter(row => row.length >= requiredHeaders.length).map(row => {
        const rowData = {};
        headers.forEach((header, index) => {
          rowData[header] = row[index] || '';
        });
        return rowData;
      });

      setPreviewData(data.slice(0, 3)); // Show first 3 rows
    };
    reader.readAsText(selectedFile);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a CSV file.');
      return;
    }

    setUploading(true);
    setError('');
    setProgress(0);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim()));
      const headers = rows[0]?.map(h => h.toLowerCase());
      const data = rows.slice(1).filter(row => row.length >= headers.length).map(row => {
        const rowData = {};
        headers.forEach((header, index) => {
          rowData[header] = row[index] || '';
        });
        return {
          name: rowData.name || 'Unknown',
          age: rowData.age || 'N/A',
          facility: rowData.facility || 'N/A',
          lga: rowData.lga || 'N/A',
          jobRole: rowData.jobrole || 'N/A',
        };
      });

      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        setProgress(i);
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      try {
        onSubmit(data);
        onClose();
      } catch (error) {
        setError('Failed to upload users. Please try again.');
      } finally {
        setUploading(false);
        setProgress(0);
      }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const template = `name,age,facility,lga,jobRole\nJohn Doe,30,General Hospital,Lagos Mainland,Nurse\nJane Smith,28,Community Clinic,Ikeja,Doctor`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="bulk-upload-modal-overlay">
      <div className="bulk-upload-modal">
        <div className="modal-header">
          <h2>Bulk Upload {type === 'health-workers' ? 'Health Workers' : 'Admins'}</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close modal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p className="modal-instructions">
            Upload a CSV file with columns: name, age, facility, lga, jobRole.{' '}
            <button className="btn-template" onClick={downloadTemplate} aria-label="Download CSV template">
              Download template
            </button>
          </p>
          <div className="file-input-wrapper">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={uploading}
              className="file-input"
              id="bulk-upload-input"
              aria-label="Upload CSV file"
            />
            <label htmlFor="bulk-upload-input" className="file-input-label">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="upload-icon">
                <path d="M19 13v6H5v-6H3v8h18v-8h-2zm-6-10h-2v8.586L6.707 7.293 5.293 8.707 12 15.414l6.707-6.707-1.414-1.414L13 11.586V3z"/>
              </svg>
              {file ? file.name : 'Choose CSV File'}
            </label>
          </div>
          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="error-icon">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          )}
          {previewData.length > 0 && (
            <div className="preview-table">
              <h3>Preview (First {previewData.length} Rows)</h3>
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Facility</th>
                    <th>LGA</th>
                    <th>Job Role</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.name || 'Unknown'}</td>
                      <td>{row.age || 'N/A'}</td>
                      <td>{row.facility || 'N/A'}</td>
                      <td>{row.lga || 'N/A'}</td>
                      <td>{row.jobrole || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {uploading && (
            <div className="progress-bar">
              <div className="progress" style={{ width: `${progress}%` }}></div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            className="btn-cancel"
            onClick={onClose}
            disabled={uploading}
            aria-label="Cancel upload"
          >
            Cancel
          </button>
          <button
            className="btn-upload"
            onClick={handleSubmit}
            disabled={uploading || !file}
            aria-label="Upload CSV file"
          >
            {uploading ? (
              <>
                <span className="spinner"></span> Uploading...
              </>
            ) : (
              'Upload'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;